import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { Search, User } from 'lucide-react';
import { useDashboardStore } from '../../store/dashboardStore';
import { Patient } from '../../types';
import { getAppointmentsDirect } from '../../lib/api';
import { AppointmentRow } from '../../types';
import { useMobileEdgeSwipeBack } from '../../hooks/useMobileEdgeSwipeBack';
import { Button } from '../ui/button';

interface SchedulePatientListPanelProps {
  isMobile?: boolean;
}

export const SchedulePatientListPanel: React.FC<SchedulePatientListPanelProps> = ({ isMobile = false }) => {
  const panelVariants: Variants = isMobile 
    ? {
        hidden: { x: '100%', opacity: 1 },
        hiddenBack: { x: '-100%', opacity: 1 },
        visible: { x: 0, opacity: 1 },
        exitBack: {
          x: '100%',
          opacity: 0,
          transition: { duration: 0.35, ease: 'easeOut' },
        },
        exitForward: { x: '-100%' },
      }
    : {
        hidden: { x: '-100%' },  // デスクトップ版は左から
        visible: { x: 0 },
      };

  const { allPatients, reserveSelectedSlotWithPatient, scheduleSelectedTime, scheduleSelectedStaffId, selectedDate, allStaff, currentClinicId, showSettingsDatePanel, isNavigatingBack, navigateBackToSchedulePanel, loadScheduleAppointments } = useDashboardStore();

  const panelContext: 'settings' | 'workflow' = showSettingsDatePanel ? 'settings' : 'workflow';

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedPatientId, setExpandedPatientId] = useState<string | null>(null);
  const [reserveError, setReserveError] = useState<string | null>(null);
  const [reserveLoading, setReserveLoading] = useState(false);
  const [reserveSuccessInfo, setReserveSuccessInfo] = useState<{ staffName: string; dateStr: string; time: string; patientName: string } | null>(null);
  // 予約済み患者情報：患者ID → 予約情報（日付、時間、スタッフID）
  const [bookedPatients, setBookedPatients] = useState<Map<string, { date: string; time: string; staffId: string; staffName?: string }>>(new Map());

  // 予約済み患者情報の取得
  useEffect(() => {
    const loadBookedPatients = async () => {
      if (!selectedDate) {
        setBookedPatients(new Map());
        return;
      }
      const ymd = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth()+1).padStart(2,'0')}-${String(selectedDate.getDate()).padStart(2,'0')}`;
      try {
        // スタッフに関係なく、すべての予約を取得
        const list = await getAppointmentsDirect(ymd, undefined, currentClinicId ?? undefined);
        const bookedMap = new Map<string, { date: string; time: string; staffId: string; staffName?: string }>();
        for (const appointment of list as AppointmentRow[]) {
          const patientId = String(appointment.patient_id);
          const existingBooking = bookedMap.get(patientId);
          // 既に登録されている場合は最初の1件のみ保持（複数予約がある場合）
          if (!existingBooking) {
            bookedMap.set(patientId, {
              date: appointment.appointment_date,
              time: appointment.appointment_time,
              staffId: appointment.doctor_id,
              staffName: appointment.doctor_name || undefined,
            });
          }
        }
        setBookedPatients(bookedMap);
      } catch (e) {
        console.error('予約済み患者の取得に失敗しました', e);
        setBookedPatients(new Map());
      }
    };
    loadBookedPatients();
  }, [selectedDate, currentClinicId]);

  const patients: Patient[] = useMemo((): Patient[] => {
    const activePatients = allPatients.filter((p: Patient) => p.isActive !== false);
    if (!searchQuery) return activePatients;
    const q = searchQuery.toLowerCase();
    return activePatients.filter((p: Patient) => {
      const nameMatch = p.name.toLowerCase().includes(q);
      const romajiMatch = p.nameRomaji.toLowerCase().startsWith(q);
      return nameMatch || romajiMatch;
    });
  }, [allPatients, searchQuery]);

  const handleRowClick = (patientId: string) => {
    setExpandedPatientId((prev) => (prev === patientId ? null : patientId));
  };

  // 日付を「〇月〇日（〇）」形式にフォーマット
  const formatDateJP = (date: Date | null): string => {
    if (!date) return '';
    const months = date.getMonth() + 1;
    const day = date.getDate();
    const week = ['日', '月', '火', '水', '木', '金', '土'][date.getDay()];
    return `${months}月${day}日（${week}）`;
  };

  // スタッフ名を取得
  const getStaffName = (staffId: string): string => {
    const staff = allStaff.find(s => String(s.id) === String(staffId));
    return staff?.name || '—';
  };

  const handleCloseReserveSuccess = () => {
    setReserveSuccessInfo(null);
    navigateBackToSchedulePanel();
  };

  const patientListSwipeRef = useRef<HTMLDivElement>(null);
  const handlePatientListEdgeBack = useCallback(() => {
    navigateBackToSchedulePanel();
  }, [navigateBackToSchedulePanel]);

  const { swipeStyle: patientListSwipeStyle } = useMobileEdgeSwipeBack(patientListSwipeRef, {
    isMobile: !!isMobile,
    enabled: !!isMobile && !reserveSuccessInfo,
    onNavigateBack: handlePatientListEdgeBack,
  });

  return (
    <>
      {/* 予約完了ポップアップ（中央揃え） */}
      {reserveSuccessInfo && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onClick={handleCloseReserveSuccess}>
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-2 text-gray-800">
              <p>{reserveSuccessInfo.staffName}</p>
              <p>{reserveSuccessInfo.dateStr} {reserveSuccessInfo.time}</p>
              <p>{reserveSuccessInfo.patientName}</p>
              <p className="pt-2 font-semibold text-green-600">予約完了しました。</p>
            </div>
            <Button type="button" variant="cta" className="mt-4 w-full" onClick={handleCloseReserveSuccess}>
              OK
            </Button>
          </div>
        </div>
      )}
    <div
      ref={patientListSwipeRef}
      style={isMobile ? patientListSwipeStyle : undefined}
      className={isMobile ? 'fixed top-0 left-0 right-0 bottom-0 pt-[var(--header-actual,var(--header-total))] z-[45]' : 'contents'}
    >
    <motion.div
      variants={panelVariants}
      initial={isMobile && isNavigatingBack ? "hiddenBack" : "hidden"}
      animate="visible"
      exit={isMobile ? (isNavigatingBack ? 'exitBack' : 'exitForward') : undefined}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={isMobile 
        ? "h-full w-full bg-white flex flex-col shadow-lg" 
        : `panel-col ${panelContext === 'settings' ? 'panel-col--settings' : 'panel-col--workflow'} z-0 bg-white border-r flex flex-col shadow-lg`
      }
    >
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {isMobile ? (
            <button
              onClick={() => {
                navigateBackToSchedulePanel();
              }}
              className="text-lg font-semibold text-gray-800 hover:text-gray-600 transition-colors cursor-pointer"
            >
              予約登録
            </button>
          ) : (
            <h2 className="text-lg font-semibold text-gray-800">予約登録</h2>
          )}
        </div>
      </div>

      {/* 検索 */}
      <div className="p-4 border-b">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="患者を検索..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* 一覧 */}
      <div className={`flex-1 overflow-y-auto ${isMobile ? 'pb-[var(--bottom-menu-total)]' : ''}`}>
        <div className="flex flex-col p-4 space-y-2">
          <AnimatePresence>
            {patients.map((p) => {
              const expanded = expandedPatientId === p.id;
              const isBooked = bookedPatients.has(String(p.id));
              const bookingInfo = isBooked ? bookedPatients.get(String(p.id)) : null;
              
              return (
                <div
                  key={p.id}
                  className={`w-full rounded-lg border ${
                    isBooked
                      ? 'bg-gray-100 border-gray-200'
                      : expanded
                        ? 'bg-green-50 border-green-200'
                        : 'bg-white border-gray-200'
                  }`}
                >
                  <motion.button
                    type="button"
                    onClick={() => handleRowClick(p.id)}
                    layout
                    initial={{ opacity: 0, y: -20, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    className={`w-full p-2 text-left origin-top ${expanded ? '' : 'hover:bg-gray-50'}`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isBooked ? 'bg-gray-200' : 'bg-green-100'
                      }`}>
                        <User size={20} className={isBooked ? 'text-gray-400' : 'text-green-600'} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{p.name}</div>
                        <div className="text-sm text-gray-500 mt-0.5 truncate">{p.nameRomaji}</div>
                      </div>
                    </div>
                  </motion.button>

                  {/* インライン展開 */}
                  <AnimatePresence initial={false}>
                    {expanded && (
                      <motion.div
                        key="expanded"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="px-3 pb-3"
                      >
                        <div className="mt-1">
                          <div className="text-sm text-gray-800 space-y-1 ml-[3.25rem]">
                            {p.patientNumber && (
                              <div>{p.patientNumber}</div>
                            )}
                            <div>{p.address || '—'}</div>
                            <div>{p.patientPhoneNumber || p.phoneNumber || '—'}</div>
                            {p.hasGuardian && (
                              <div className="mt-2 p-3 border border-gray-300 rounded-md bg-gray-50">
                                <div className="font-medium text-gray-700 mb-2">保護者情報</div>
                                {p.guardianName && (
                                  <div>
                                    {p.guardianName}
                                    {p.guardianRelationship && `（${p.guardianRelationship}）`}
                                  </div>
                                )}
                                {p.guardianNameRomaji && (
                                  <div>{p.guardianNameRomaji}</div>
                                )}
                                {p.guardianContact && (
                                  <div>{p.guardianContact}</div>
                                )}
                              </div>
                            )}
                            {/* 登録済み情報 */}
                            {isBooked && bookingInfo && selectedDate && (
                              <div className="mt-2">
                                <div className="font-medium text-gray-700 mb-1">登録済み</div>
                                <div className="text-sm text-gray-600">
                                  {formatDateJP(selectedDate)} {bookingInfo.time} {bookingInfo.staffName || getStaffName(bookingInfo.staffId)}
                                </div>
                              </div>
                            )}
                          </div>
                          {/* 予約ボタン（登録済みの場合は表示しない） */}
                          {!isBooked && (
                            <>
                              {reserveError && expandedPatientId === p.id && (
                                <p className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-2 py-1">
                                  {reserveError}
                                </p>
                              )}
                              <div className="mt-3 flex justify-end">
                                <button
                                  type="button"
                                  className="px-3 py-1.5 text-sm font-semibold rounded-md border border-green-400 text-green-700 bg-white hover:bg-green-50 hover:border-green-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  disabled={reserveLoading}
                                  onClick={async () => {
                                    setReserveError(null);
                                    if (!scheduleSelectedStaffId) {
                                      setReserveError('スタッフが未選択です');
                                      return;
                                    }
                                    if (!selectedDate) {
                                      setReserveError('日付が未選択です');
                                      return;
                                    }
                                    if (!scheduleSelectedTime) {
                                      setReserveError('時間が未選択です');
                                      return;
                                    }
                                    setReserveLoading(true);
                                    setReserveError(null);
                                    try {
                                      const ok = await reserveSelectedSlotWithPatient(p.id);
                                      if (ok) {
                                        const staffName = getStaffName(scheduleSelectedStaffId || '');
                                        const dateStr = formatDateJP(selectedDate);
                                        setReserveSuccessInfo({
                                          staffName,
                                          dateStr,
                                          time: scheduleSelectedTime || '',
                                          patientName: p.name,
                                        });
                                        loadScheduleAppointments(); // スケジュール一覧を更新
                                      } else {
                                        setReserveError('予約できませんでした');
                                      }
                                    } finally {
                                      setReserveLoading(false);
                                    }
                                  }}
                                >
                                  {reserveLoading ? '登録中...' : '予約'}
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
    </div>
    </>
  );
};


